/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect, type ComponentProps } from 'react';
import Slider, { type Settings } from 'react-slick';
import useInview from 'effects/inview';
import useKeys from 'effects/keys';
import useEnhanced from 'effects/enhanced';
import Picture from 'components/Picture/Picture';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './Thumbs.scss';

const DEFAULT_LIST: Thumb[] = [];

const DEFAULT_SLIDER: Settings = {
  dots: false,
  infinite: true,
  speed: 800,
  // react-slick types this as a string; the number is what it was given before the conversion.
  centerPadding: 0 as unknown as string,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  variableWidth: true,
  initialSlide: 0,
};

const DEFAULT_ON_SELECT = () => {};

const SLIDE_WIDTH_SMALL = 286;
const SLIDE_WIDTH_ENHANCED = 394;
const TRACK_OFFSET = 60;

interface Thumb extends ComponentProps<typeof Picture> {
  index: string | number;
  title?: string;
  duration?: string | null;
}

interface ThumbsProps {
  list?: Thumb[];
  slider?: Settings;
  next?: string;
  onSelect?: (index: string | number) => void;
}

/**
 * This is the Thumbs component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Thumbs({
  list = DEFAULT_LIST,
  slider: sliderProps = DEFAULT_SLIDER,
  next = 'Next video',
  onSelect = DEFAULT_ON_SELECT,
}: ThumbsProps) {
  const enhanced = useEnhanced(660);
  const [ref, inview] = useInview<HTMLDivElement>({ threshold: 0.6 });
  const sliderRef = useRef<Slider>(null);
  const [slider, setSlider] = useState(false);

  const slideWidth = enhanced ? SLIDE_WIDTH_ENHANCED : SLIDE_WIDTH_SMALL;
  const maxTrack = slideWidth * list.length + TRACK_OFFSET;

  useEffect(() => {
    function onResize() {
      setSlider(maxTrack > window.innerWidth);
    }

    onResize();

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [maxTrack]);

  useKeys('video-gallery-thumb', inview && slider, {
    left: e => {
      e.preventDefault();
      sliderRef.current!.slickPrev();
    },
    right: e => {
      e.preventDefault();
      sliderRef.current!.slickNext();
    },
  });

  function renderList() {
    return list.map(({ index, title, duration, ...props }) => (
      <div className="thumb" key={index}>
        <button
          onClick={() => {
            onSelect(index);
          }}
        >
          <div>
            <div className="image">
              <Picture {...props} />
              <span className="duration">{duration}</span>
            </div>
            <div className="text">
              <h3>{title}</h3>
            </div>
          </div>
        </button>
      </div>
    ));
  }

  function renderSlider() {
    if (slider) {
      return (
        <Slider ref={sliderRef} {...sliderProps}>
          {renderList()}
        </Slider>
      );
    }

    return <div className="slick-track inactive">{renderList()}</div>;
  }

  return (
    <div ref={ref} className="thumbs" appear="true">
      <h2>
        <span>{next}</span>
      </h2>
      {renderSlider()}
    </div>
  );
}

export default Thumbs;
